import isEqual from "lodash/isEqual";
import { useState, useEffect, useMemo } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  type OnDragEndResponder,
} from "@hello-pangea/dnd";
import {
  InfiniteListBase,
  RecordContextProvider,
  useDataProvider,
  useGetIdentity,
  useListContext,
  useRedirect,
  useTranslate,
} from "ra-core";
import { matchPath, useLocation } from "react-router";
import * as LucideIcons from "lucide-react";
import { ChevronRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";

import { MobileListHeader } from "../layout/MobileListHeader";
import { MobileContent } from "../layout/MobileContent";
import { InfinitePagination } from "../misc/InfinitePagination";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";
import { DealCreate } from "./DealCreate";
import { DealEdit } from "./DealEdit";
import { DealShow } from "./DealShow";
import { findDealLabel } from "./dealUtils";
import type { DealsByStage } from "./stages";
import {
  getDealsByStage,
  updateDealStageLocal,
  updateDealStageRemote,
} from "./stages";

export const MobileDealList = () => {
  const { identity } = useGetIdentity();

  if (!identity) return null;

  return (
    <InfiniteListBase
      perPage={100}
      sort={{ field: "index", order: "DESC" }}
      filter={{
        "archived_at@is": null,
        "@or": {
          "snoozed_until@is": null,
          "snoozed_until@lte": new Date().toISOString(),
        },
      }}
      queryOptions={{
        onError: () => {
          /* The layout renders the error state inline. */
        },
      }}
    >
      <MobileDealListLayout />
    </InfiniteListBase>
  );
};

const MobileDealListLayout = () => {
  const translate = useTranslate();
  const location = useLocation();
  const [createOpen, setCreateOpen] = useState(false);
  const matchShow = matchPath("/deals/:id/show", location.pathname);
  const matchEdit = matchPath("/deals/:id", location.pathname);
  const { data, isPending, error } = useListContext<Deal>();

  return (
    <>
      <MobileListHeader
        title={translate("resources.deals.name", { smart_count: 2 })}
        onCreateClick={() => setCreateOpen(true)}
      />
      <MobileContent>
        {isPending ? (
          <MobileDealSkeleton />
        ) : error ? (
          <p className="text-sm text-destructive">
            {translate("ra.message.error")}
          </p>
        ) : data?.length ? (
          <MobileDealStages deals={data} />
        ) : (
          <div className="py-10 text-center">
            <h2 className="text-lg font-semibold">
              {translate("resources.deals.empty.title")}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {translate("resources.deals.empty.description")}
            </p>
          </div>
        )}
        {!error && (
          <div className="flex justify-center">
            <InfinitePagination />
          </div>
        )}
      </MobileContent>
      <DealCreate open={createOpen} onOpenChange={setCreateOpen} />
      <DealEdit open={!!matchEdit} id={matchEdit?.params.id} />
      <DealShow open={!!matchShow} id={matchShow?.params.id} />
    </>
  );
};

const MobileDealStages = ({ deals }: { deals: Deal[] }) => {
  const { dealPipelineStatuses, dealStages, currency } =
    useConfigurationContext();
  const dataProvider = useDataProvider();
  const { refetch } = useListContext<Deal>();

  const pipelineStages = useMemo(
    () => dealStages.filter((s) => dealPipelineStatuses.includes(s.value)),
    [dealStages, dealPipelineStatuses],
  );

  const [dealsByStage, setDealsByStage] = useState<DealsByStage>(
    getDealsByStage(deals, pipelineStages),
  );

  // Deal whose stage we want to change via picker sheet
  const [pickerDeal, setPickerDeal] = useState<Deal | null>(null);

  useEffect(() => {
    const newDealsByStage = getDealsByStage(deals, pipelineStages);
    if (!isEqual(newDealsByStage, dealsByStage)) {
      setDealsByStage(newDealsByStage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deals, pipelineStages]);

  const onDragEnd: OnDragEndResponder = (result) => {
    const { destination, source } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return;

    const sourceStage = source.droppableId;
    const destinationStage = destination.droppableId;
    const sourceDeal = dealsByStage[sourceStage]?.[source.index];
    if (!sourceDeal) return;

    const destinationDeal = dealsByStage[destinationStage]?.[
      destination.index
    ] ?? {
      stage: destinationStage,
      index: undefined,
    };

    setDealsByStage(
      updateDealStageLocal(
        sourceDeal,
        { stage: sourceStage, index: source.index },
        { stage: destinationStage, index: destination.index },
        dealsByStage,
      ),
    );

    updateDealStageRemote(sourceDeal, destinationDeal, dataProvider).then(() =>
      refetch(),
    );
  };

  const handleStageChange = async (deal: Deal, newStage: string) => {
    if (deal.stage === newStage) return;
    const sourceStage = deal.stage;
    const sourceIndex =
      dealsByStage[sourceStage]?.findIndex((d) => d.id === deal.id) ?? 0;
    const destIndex = dealsByStage[newStage]?.length ?? 0;

    // Optimistic update
    setDealsByStage((prev) => {
      const sourceColumn = [...(prev[sourceStage] ?? [])];
      const destColumn = [...(prev[newStage] ?? [])];
      sourceColumn.splice(sourceIndex, 1);
      destColumn.push({ ...deal, stage: newStage });
      return { ...prev, [sourceStage]: sourceColumn, [newStage]: destColumn };
    });

    await dataProvider.update("deals", {
      id: deal.id,
      data: { stage: newStage, index: destIndex },
      previousData: deal,
    });
    refetch();
  };

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="space-y-5">
          {/* Stage summary badges */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {pipelineStages.map((stage) => {
              const stageDeals = dealsByStage[stage.value] ?? [];
              const StageIcon = stage.icon
                ? (LucideIcons as Record<string, LucideIcons.LucideIcon>)[
                    stage.icon
                  ]
                : null;
              return (
                <Badge
                  key={stage.value}
                  variant="outline"
                  className="shrink-0 gap-1"
                >
                  {StageIcon && <StageIcon className="h-3 w-3" />}
                  {stage.label} · {stageDeals.length}
                </Badge>
              );
            })}
          </div>

          {pipelineStages.map((stage) => {
            const stageDeals = dealsByStage[stage.value] ?? [];
            const StageIcon = stage.icon
              ? (LucideIcons as Record<string, LucideIcons.LucideIcon>)[
                  stage.icon
                ]
              : null;
            return (
              <section key={stage.value} className="space-y-2">
                <div className="flex items-center gap-2">
                  {StageIcon && (
                    <StageIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}
                  <h2 className="text-base font-semibold flex-1">
                    {findDealLabel(dealStages, stage.value)}
                  </h2>
                  <span className="text-sm text-muted-foreground">
                    {stageDeals.length}
                  </span>
                </div>
                <Droppable droppableId={stage.value}>
                  {(droppableProvided, snapshot) => (
                    <div
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                      className={`space-y-2 min-h-[56px] rounded-lg transition-colors ${
                        snapshot.isDraggingOver ? "bg-muted" : ""
                      }`}
                    >
                      {stageDeals.map((deal, index) => (
                        <Draggable
                          key={deal.id}
                          draggableId={String(deal.id)}
                          index={index}
                        >
                          {(dragProvided, dragSnapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              style={dragProvided.draggableProps.style}
                            >
                              <MobileDealCard
                                deal={deal}
                                stageLabel={stage.label}
                                currency={currency}
                                isDragging={dragSnapshot.isDragging}
                                onTap={() => setPickerDeal(deal)}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                      {stageDeals.length === 0 && !snapshot.isDraggingOver && (
                        <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                          Нет сделок
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </section>
            );
          })}
        </div>
      </DragDropContext>

      {/* Quick stage change sheet */}
      <Sheet open={!!pickerDeal} onOpenChange={(open) => !open && setPickerDeal(null)}>
        <SheetContent side="bottom" className="pb-safe">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-left text-base">
              {pickerDeal?.name}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {pipelineStages.map((stage) => {
              const isCurrentStage = pickerDeal?.stage === stage.value;
              const StageIcon = stage.icon
                ? (LucideIcons as Record<string, LucideIcons.LucideIcon>)[
                    stage.icon
                  ]
                : null;
              return (
                <Button
                  key={stage.value}
                  variant={isCurrentStage ? "default" : "outline"}
                  className="w-full justify-start gap-2 h-11"
                  onClick={() => {
                    if (pickerDeal) {
                      handleStageChange(pickerDeal, stage.value);
                    }
                    setPickerDeal(null);
                  }}
                >
                  {StageIcon && <StageIcon className="h-4 w-4" />}
                  {stage.label}
                  {isCurrentStage && (
                    <span className="ml-auto text-xs opacity-70">текущий</span>
                  )}
                </Button>
              );
            })}
          </div>
          <div className="mt-4 pt-2 border-t">
            <ViewDealButton deal={pickerDeal} onClose={() => setPickerDeal(null)} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

const ViewDealButton = ({
  deal,
  onClose,
}: {
  deal: Deal | null;
  onClose: () => void;
}) => {
  const redirect = useRedirect();
  if (!deal) return null;
  return (
    <Button
      variant="ghost"
      className="w-full justify-between"
      onClick={() => {
        onClose();
        redirect(`/deals/${deal.id}/show`);
      }}
    >
      Открыть сделку
      <ChevronRight className="h-4 w-4" />
    </Button>
  );
};

const MobileDealCard = ({
  deal,
  stageLabel,
  currency,
  isDragging,
  onTap,
}: {
  deal: Deal;
  stageLabel: string;
  currency: string;
  isDragging?: boolean;
  onTap: () => void;
}) => {
  const { dealCategories } = useConfigurationContext();
  const categoryLabel =
    dealCategories.find((category) => category.value === deal.category)
      ?.label ?? deal.category;

  return (
    <RecordContextProvider value={deal}>
      <Card
        className={`py-3 transition-shadow ${isDragging ? "shadow-lg" : ""}`}
        role="button"
        tabIndex={0}
        onClick={onTap}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onTap();
          }
        }}
      >
        <CardContent className="px-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-medium leading-5 flex-1">
              {deal.name}
            </h3>
            <Badge variant="secondary" className="shrink-0 text-xs">
              {stageLabel}
            </Badge>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>
              {Number(deal.amount ?? 0).toLocaleString("ru-RU", {
                notation: "compact",
                style: "currency",
                currency,
                currencyDisplay: "narrowSymbol",
                minimumSignificantDigits: 3,
              })}
            </span>
            {categoryLabel ? <span>{categoryLabel}</span> : null}
            {deal.expected_closing_date ? (
              <span>{deal.expected_closing_date}</span>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </RecordContextProvider>
  );
};

const MobileDealSkeleton = () => (
  <div className="space-y-3">
    <Skeleton className="h-6 w-2/3" />
    <Skeleton className="h-24 w-full" />
    <Skeleton className="h-24 w-full" />
    <Skeleton className="h-6 w-1/2" />
    <Skeleton className="h-24 w-full" />
  </div>
);
